import type { Reservation } from '../store/useStore'

// #1024 (F1b): 予約は Firestore 直読から API 取得に一本化した。API(FastAPI) は
// Firestore の Timestamp を ISO 文字列にシリアライズして返すため、旧 onSnapshot 時代の
// `.toDate().toISOString()` 変換は不要 —— 文字列をそのまま採用する。
// useMyReservations / useStoreReservations が同一フィールドをマップするため共通化。

/** API から受け取った予約 dict を frontend の Reservation 型へ正規化する。 */
export function mapApiReservation(data: Record<string, unknown>): Reservation {
  const str = (v: unknown): string | undefined =>
    typeof v === 'string' ? v : undefined
  return {
    id: data.id as string,
    storeId: data.storeId as string,
    customerId: data.customerId as string,
    date: data.date as Reservation['date'],
    time: data.time as Reservation['time'],
    partySize: data.partySize as Reservation['partySize'],
    contactName: data.contactName as Reservation['contactName'],
    contactEmail: data.contactEmail as Reservation['contactEmail'],
    contactPhone: data.contactPhone as Reservation['contactPhone'],
    notes: data.notes as Reservation['notes'],
    status: data.status as Reservation['status'],
    cancelledBy: data.cancelledBy as Reservation['cancelledBy'],
    cancelledAt: str(data.cancelledAt),
    reasonCode: data.reasonCode as Reservation['reasonCode'],
    slotClosed: data.slotClosed as Reservation['slotClosed'],
    // #737 / #738: course + payment fields
    courseId: data.courseId as Reservation['courseId'],
    courseName: data.courseName as Reservation['courseName'],
    // #1644: コース内容 + 飲み放題プランのスナップショット (予約履歴での表示用)
    courseDishList: data.courseDishList as Reservation['courseDishList'],
    drinkPlanName: data.drinkPlanName as Reservation['drinkPlanName'],
    drinkPlanPriceYen: data.drinkPlanPriceYen as Reservation['drinkPlanPriceYen'],
    totalAmount: data.totalAmount as Reservation['totalAmount'],
    paymentTiming: data.paymentTiming as Reservation['paymentTiming'],
    paymentStatus: data.paymentStatus as Reservation['paymentStatus'],
    depositAmount: data.depositAmount as Reservation['depositAmount'],
    longTermFallbackApplied: data.longTermFallbackApplied as Reservation['longTermFallbackApplied'],
    captureMethod: data.captureMethod as Reservation['captureMethod'],
    paymentIntentId: data.paymentIntentId as Reservation['paymentIntentId'],
    // #2014 ポイントで払った予約か (lib/paymentMethods の isPointsMethod)。変更の受付可否の判定に使う
    paymentMethod: (data.paymentMethod as string | undefined) ?? undefined,
    stripeAccountId: data.stripeAccountId as Reservation['stripeAccountId'],
    // #745 / #746: 変更機能 / 公開共有 URL
    shortCode: data.shortCode as Reservation['shortCode'],
    additionalPaymentIntentIds: data.additionalPaymentIntentIds as Reservation['additionalPaymentIntentIds'],
    changeHistory: data.changeHistory as Reservation['changeHistory'],
    // 承認済み予約への変更の再承認待ち (却下で元に戻る) / 却下時の店舗メッセージ
    pendingChange: (data.pendingChange as Reservation['pendingChange']) ?? null,
    storeMessage: (data.storeMessage as string | undefined) ?? undefined,
    // #1504 キャンセル後の「いくら返ったか」はサーバが Stripe の結果から書く値が真実
    refundStatus: data.refundStatus as Reservation['refundStatus'],
    refundedAmount: typeof data.refundedAmount === 'number' ? data.refundedAmount : undefined,
    // #2010 店舗画面の「返金に失敗 (¥N)」は 約束 − 実績。受け取っていないと常に ¥0 になる
    refundIntendedAmount: typeof data.refundIntendedAmount === 'number' ? data.refundIntendedAmount : undefined,
    paidAmount: typeof data.paidAmount === 'number' ? data.paidAmount : undefined,
    // #2226 カードに返せない返金の判定 (refundError の理由) と、引換券のお知らせ・引き換えの記録。
    // **写さないと画面の分岐が全部動かない** (お客様に引換券が出ず、お店にも案内が出ない)
    refundError: str(data.refundError),
    // #2476 申し立て (チャージバック) が来ている (サーバの判断)。写さないと「引換券をお見せください」のまま
    refundDisputed: data.refundDisputed === true ? true : undefined,
    refundAlternate: (data.refundAlternate as Reservation['refundAlternate']) ?? undefined,
    refundUnreachableNotice: (data.refundUnreachableNotice as Reservation['refundUnreachableNotice']) ?? undefined,
    refundVoucherId: str(data.refundVoucherId),
    createdAt: str(data.createdAt) ?? '',
    updatedAt: str(data.updatedAt) ?? '',
  }
}
